import { useState } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useGetCancellationPolicyFeatureFlagDetails, useGetReschedulingRulesAccess } from '@frontend/schedule-helpers';
import { SettingsSection } from '@frontend/settings-ui';
import { sentry } from '@frontend/tracking';
import { Button, SpinningLoader, useAlert, useFormField } from '@frontend/design-system';
import {
  CANCELLATION_POLICY_DEFAULT_DURATION,
  CANCELLATION_POLICY_DEFAULT_TIME_UNIT,
  CANCELLATION_POLICY_MAX_DAYS,
  CANCELLATION_POLICY_MAX_HOURS,
} from '../../components/duration-time-unit-selection/constants';
import {
  convertMinutesToDaysAndHours,
  convertDaysAndHoursToMinutes,
  getMaximumDurationError,
} from '../../components/duration-time-unit-selection/helpers';
import { CancellationPolicyText } from './CancellationPolicyText';
import { ReschedulingRules } from './ReschedulingRules';

enum ReschedulingRulesOptions {
  WITHIN_WINDOW = 'WITHIN_WINDOW',
  OFFICE_CALL_ONLY = 'OFFICE_CALL_ONLY',
  ANYTIME = 'ANYTIME',
}

const DEFAULT_RESCHEDULE_RULE: ReschedulingRulesOptions.WITHIN_WINDOW = ReschedulingRulesOptions.WITHIN_WINDOW;

export const CancellationPolicy = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const [hasEditedPolicyText, setHasEditedPolicyText] = useState(false);
  const { isCancellationPolicyEnabled, isLoading: isLoadingPolicyTextAccess } =
    useGetCancellationPolicyFeatureFlagDetails();

  const { hasReschedulingRulesAccess, isLoading: isReschedulingRulesAccessLoading } = useGetReschedulingRulesAccess();

  const { data, isLoading } = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds: [locationId] },
    options: { enabled: !!locationId },
  });

  const locationSettings = data?.locationsSettings?.[locationId];

  const { mutateAsync, isLoading: isSaving } = SchedulerV3.Mutations.useUpdateLocationSettingsMutation();

  const reschedulingRules = locationSettings?.reschedulingRules;

  const savedPolicyText = locationSettings?.cancellationPolicyText ?? '';

  const windowMinutes = reschedulingRules?.reschedulingWindowDurationMinutes ?? 0;

  const { duration: initialDuration, timeUnit: initialTimeUnit } =
    windowMinutes > 0
      ? convertMinutesToDaysAndHours(windowMinutes)
      : { duration: CANCELLATION_POLICY_DEFAULT_DURATION, timeUnit: CANCELLATION_POLICY_DEFAULT_TIME_UNIT };

  const rescheduleTypeFieldProps = useFormField(
    {
      type: 'radio',
      value: reschedulingRules?.reschedulingPolicy || DEFAULT_RESCHEDULE_RULE,
    },
    [reschedulingRules?.reschedulingPolicy]
  );

  const restrictionDurationFieldProps = useFormField(
    {
      type: 'number',
      value: String(initialDuration),
      min: 0,
      allowDecimal: false,
      allowNegative: false,
    },
    [initialDuration]
  );

  const restrictionTimeUnitFieldProps = useFormField(
    {
      type: 'dropdown',
      value: initialTimeUnit,
    },
    [initialTimeUnit]
  );

  const cancellationPolicyText = useFormField(
    {
      type: 'text',
      value: savedPolicyText,
    },
    [savedPolicyText]
  );

  const getDynamicPolicyText = () => {
    if (!savedPolicyText) {
      if (rescheduleTypeFieldProps.value === ReschedulingRulesOptions.WITHIN_WINDOW) {
        return t(
          'Appointments cancelled within {{count}} {{timeUnit}} of the scheduled time may be subject to a cancellation fee.',
          {
            count: +restrictionDurationFieldProps.value || CANCELLATION_POLICY_DEFAULT_DURATION,
            timeUnit: restrictionTimeUnitFieldProps.value || CANCELLATION_POLICY_DEFAULT_TIME_UNIT,
          }
        );
      } else if (rescheduleTypeFieldProps.value === ReschedulingRulesOptions.OFFICE_CALL_ONLY) {
        return t(
          'Appointments cannot be cancelled or rescheduled online. Please call our office directly for assistance.'
        );
      } else {
        return '';
      }
    }
    return '';
  };

  const dynamicPolicyText = hasReschedulingRulesAccess ? getDynamicPolicyText() : '';
  const policyText = hasEditedPolicyText
    ? cancellationPolicyText.value
    : cancellationPolicyText.value || dynamicPolicyText;

  const handleSave = async () => {
    const reschedulingRulesPayload = hasReschedulingRulesAccess
      ? {
          reschedulingPolicy: rescheduleTypeFieldProps.value,
          reschedulingWindowDurationMinutes:
            rescheduleTypeFieldProps.value === ReschedulingRulesOptions.WITHIN_WINDOW
              ? convertDaysAndHoursToMinutes(
                  Number(restrictionDurationFieldProps.value),
                  restrictionTimeUnitFieldProps.value as 'days' | 'hours'
                ) ?? 0
              : 0,
        }
      : undefined;

    try {
      await mutateAsync({
        locationId,
        ...(isCancellationPolicyEnabled && {
          cancellationPolicyText: policyText,
        }),
        ...(hasReschedulingRulesAccess && {
          reschedulingRules: reschedulingRulesPayload,
        }),
      });
      alert.success(t('Settings saved successfully'));
    } catch (error) {
      sentry.error({
        error,
        team: 'schedule',
        topic: 'scheduling',
        addContext: {
          name: 'cancellation-policy-page-save-failure',
          context: {
            locationId,
            isCancellationPolicyEnabled,
            hasReschedulingRulesAccess,
            ...(isCancellationPolicyEnabled && {
              submittedPolicyLength: policyText.length,
              previousPolicyLength: savedPolicyText.length,
              isClearingPolicy: !!savedPolicyText && policyText.length === 0,
              isUsingDynamicPolicyText: !cancellationPolicyText.value && !!dynamicPolicyText,
            }),
            ...(hasReschedulingRulesAccess && {
              reschedulingPolicy: rescheduleTypeFieldProps.value,
              reschedulingWindowDurationMinutes: reschedulingRulesPayload?.reschedulingWindowDurationMinutes,
            }),
          },
        },
      });
      alert.error(t('Failed to save settings'));
    }
  };

  const restrictionErrorText =
    rescheduleTypeFieldProps.value === ReschedulingRulesOptions.WITHIN_WINDOW
      ? getMaximumDurationError({
          value: Number(restrictionDurationFieldProps.value),
          timeUnit: restrictionTimeUnitFieldProps.value as 'days' | 'hours',
          maxHours: CANCELLATION_POLICY_MAX_HOURS,
          maxDays: CANCELLATION_POLICY_MAX_DAYS,
        })
      : '';

  const isSaveDisabled = isLoading || isSaving || (hasReschedulingRulesAccess && !!restrictionErrorText);

  if (isLoading || isLoadingPolicyTextAccess || isReschedulingRulesAccessLoading) {
    return <SpinningLoader />;
  }

  return (
    <SettingsSection>
      {hasReschedulingRulesAccess && (
        <ReschedulingRules
          restrictionDurationFieldProps={restrictionDurationFieldProps}
          restrictionTimeUnitFieldProps={restrictionTimeUnitFieldProps}
          rescheduleTypeFieldProps={rescheduleTypeFieldProps}
          restrictionErrorText={restrictionErrorText}
        />
      )}
      {isCancellationPolicyEnabled && hasReschedulingRulesAccess && (
        <hr
          css={{ margin: `var(--fabric-space-gap-lg) 0`, border: `1px solid var(--fabric-color-accent-neutral-muted)` }}
        />
      )}
      {isCancellationPolicyEnabled && (
        <CancellationPolicyText
          fieldProps={cancellationPolicyText}
          value={policyText}
          onChange={() => setHasEditedPolicyText(true)}
        />
      )}
      <section css={{ marginTop: `var(--fabric-space-gap-lg)`, display: `flex`, justifyContent: `flex-end` }}>
        <div css={{ width: `var(--fabric-space-gap-sm)` }} />
        <Button variant='primary' onClick={handleSave} disabled={isSaveDisabled}>
          {t('Save')}
        </Button>
      </section>
    </SettingsSection>
  );
};
